/**
 * navConfig.js — the main sidebar's registry: which pages exist, how they
 * group, in what order they appear, and which role sees which page by default.
 *
 * Pure data and pure functions, no React. AppSidebar renders whatever
 * buildNavTree() hands it, and __tests__/sidebar/navConfig.test.js pins down
 * the exact rail every role gets — change a list here and that test is the
 * place that tells you who was affected.
 *
 * ── THE GROUPING RULE ──────────────────────────────────────────────────────
 * A group exists on screen only when the user can see at least TWO of its
 * pages. With exactly one visible page, that page stands alone in the group's
 * slot (no "Metrics" folder that opens onto a single entry); with none, the
 * slot disappears. The rule runs on the user's EFFECTIVE set of pages — role
 * defaults, admin pagePermissions overrides and the API-driven entries
 * (scores, everything) alike — never on the role name. So a plain agent who
 * has only Goals sees "Goals", and the moment an admin grants them KYC Goals
 * too, the two fold into "Metrics" without anyone touching this file.
 */

/* ------------------------------ Pages --------------------------------- */

/** Every page the rail can show. `iconName` keys into NikeNavIcons. */
import { canUseSimulations } from '../../config/simulationsAccess';

export const NAV_ITEMS = [
  { id: 'home', iconName: 'home', label: 'Home', path: '/home' },
  // Customer Support
  { id: 'tools', iconName: 'tools', label: 'Tools', path: '/tools' },
  { id: 'acp', iconName: 'acp', label: 'ACP', path: '/acp' },
  { id: 'simulations', iconName: 'simulations', label: 'Simulations', path: '/simulations' },
  { id: 'knowledge-base', iconName: 'knowledge-base', label: 'Knowledge Base', path: '/knowledge-base' },
  // Metrics
  { id: 'goals', iconName: 'goals', label: 'Goals', path: '/goals' },
  { id: 'kyc-goals', iconName: 'kyc-goals', label: 'KYC Goals', path: '/kyc-goals' },
  { id: 'payments-goals', iconName: 'payments-goals', label: 'Payments Goals', path: '/payments-goals' },
  { id: 'crypto-goals', iconName: 'crypto-goals', label: 'Crypto Goals', path: '/crypto-goals' },
  { id: 'vip-host-metrics', iconName: 'vip-host-metrics', label: 'VIP Host Metrics', path: '/vip-host-metrics' },
  { id: 'kyc-stats', iconName: 'kyc-stats', label: 'KYC Stats', path: '/kyc-stats' },
  // QA
  { id: 'tl', iconName: 'tl', label: 'TLs', path: '/tl' },
  { id: 'qa-manager', iconName: 'qa-manager', label: 'QA Manager', path: '/qa-manager' },
  { id: 'scores', iconName: 'scores', label: 'Scores', path: '/scores' },
  // Standalone
  { id: 'everything', iconName: 'everything', label: 'Everything & Anything', path: '/everything' },
  // Devs
  { id: 'developer-dashboard', iconName: 'developer-dashboard', label: 'Developer Dashboard', path: '/developer-dashboard' },
  { id: 'monitoring', iconName: 'monitoring', label: 'Monitoring', path: '/monitoring' },
  { id: 'clara-ai-dashboard', iconName: 'clara-ai-dashboard', label: 'Clara AI', path: '/clara-ai-dashboard' },
  { id: 'user-management', iconName: 'user-management', label: 'User Management', path: '/user-management' },
];

export const NAV_ITEM_BY_ID = Object.fromEntries(NAV_ITEMS.map((item) => [item.id, item]));

/* ------------------------------ Groups -------------------------------- */

/**
 * The folders. `items` is the in-group order. Group ids must not collide with
 * page ids (both appear as keys of the open-groups state and in NAV_ORDER).
 */
export const NAV_GROUPS = [
  {
    // Customer Support (2026-09-16): the agent-facing workbench — the Tools
    // dashboard, the standalone ACP page and Simulations (2026-11-03: practise
    // real cases with Clara playing the player).
    id: 'cs',
    iconName: 'cs',
    label: 'Customer Support',
    items: ['tools', 'acp', 'simulations'],
  },
  {
    id: 'metrics',
    iconName: 'metrics',
    label: 'Metrics',
    items: ['goals', 'kyc-goals', 'payments-goals', 'crypto-goals', 'vip-host-metrics', 'kyc-stats'],
  },
  {
    id: 'qa',
    iconName: 'qa',
    label: 'QA',
    items: ['tl', 'qa-manager', 'scores'],
  },
  {
    id: 'devs',
    iconName: 'devs',
    label: 'Devs',
    items: ['developer-dashboard', 'monitoring', 'clara-ai-dashboard', 'user-management'],
  },
];

export const NAV_GROUP_BY_ID = Object.fromEntries(NAV_GROUPS.map((group) => [group.id, group]));

/**
 * Top-to-bottom order of the rail. A group slot is where the folder appears
 * for someone who sees several of its pages — and where the lone page appears
 * for someone who sees just one — so the rail keeps the same shape for every
 * role: Home, Customer Support (Tools + ACP), Knowledge Base, then metrics,
 * then QA, then the hub, then the developer pages.
 */
export const NAV_ORDER = [
  { type: 'item', id: 'home' },
  { type: 'group', id: 'cs' },
  { type: 'item', id: 'knowledge-base' },
  { type: 'group', id: 'metrics' },
  { type: 'group', id: 'qa' },
  { type: 'item', id: 'everything' },
  { type: 'group', id: 'devs' },
];

/** Group a page belongs to, or null for standalone pages. */
export function groupIdOf(itemId) {
  const group = NAV_GROUPS.find((g) => g.items.includes(itemId));
  return group ? group.id : null;
}

/* ------------------------------ Access -------------------------------- */

/**
 * Pages whose visibility is decided by an API answer rather than the role
 * map: 'scores' via /api/scores/my-access (MAESTRO_CUTOVER_OFF gate — admins
 * always, everyone once the cutover is done), 'everything' via
 * /api/everything/my-access (≥1 granted card, or admin/developer) and 'acp'
 * via /api/auth/acp-access (the login email has a stake.com ACP account with
 * a support-team role — clarab/services/acpAccessService.js; admin/developer
 * always).
 */
export const API_DRIVEN_IDS = ['scores', 'everything', 'acp'];

/**
 * Default page access per role — the PRIMARY source; pagePermissions is an
 * OVERRIDE on top of it (see hasPagePermission). Ids not in NAV_ITEMS
 * ('workspaces', 'chat') are routes without a rail entry; they stay in these
 * lists because pagePermissions and the User Management defaults read them.
 *
 * Client-side lists are ADVISORY: every page here is also gated server-side
 * (PrivateRoute requiredRoles in App.js + the API's own guards). Keep the two
 * in step — an entry here that the route 403s is a broken icon, not access.
 */
export const ROLE_ACCESS = {
  // TEMP (2026-06-16): 'user-management' restricted to ADMIN ONLY while testing,
  // so it was removed from these roles' sidebar access: kyc-admin, tl, vip-host-tl,
  // qa-admin, developer. Restore 'user-management' to each of them when testing is done.
  // Keep in sync with backend auth.js `userMgmt` guard + App.js requiredRoles.
  'user': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'goals'],
  'kyc-user': ['home', 'kyc-goals', 'goals'],
  // kyc-admin / payments-admin get the TL surface too. What they actually
  // see there is driven by their TLTeamAssignment row (/tl/admin), not by
  // the role — no assignment means an empty dashboard, not an error.
  // kyc-admin / payments-admin get Tools too (Filip, 2026-09-11) — the team admins use the same
  // lookups (VIP progress, hash explorer, active bets…) their agents do.
  // kyc-admin also sees KYC Stats — the team-wide analytics page (Filip, 2026-09-18);
  // the route guard in App.js and the API gate (middleware/auth.js kycStats) admit the same role.
  'kyc-admin': ['home', 'tools', 'kyc-goals', 'kyc-stats', 'goals', 'tl'],
  'payments-user': ['home', 'payments-goals', 'goals'],
  'payments-admin': ['home', 'tools', 'payments-goals', 'goals', 'tl'],
  // crypto-user / crypto-admin (2026-09-10): the /crypto-goals pair. Unlike
  // kyc-admin / payments-admin there is no 'tl' here — the crypto team sits
  // outside the CS team-leader structure (Filip), and 'tl' itself does not
  // get crypto-goals either. Server side: featureAccess('crypto-goals').
  'crypto-user': ['home', 'crypto-goals', 'goals'],
  'crypto-admin': ['home', 'crypto-goals', 'goals'],
  // TLs grade their agents with NOTES inside QA Manager (note-only ticket
  // dialog, internal release — see QAManagerContext.startGrading).
  'tl': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'tl', 'qa-manager', 'goals', 'payments-goals'],
  // 'kick-tl' is role 'tl' MINUS qa-manager (and minus payments-goals,
  // which has nothing to do with Kick). That subtraction is the entire
  // reason the role exists — the Kick org runs no QA Manager. It is a hard
  // server-side denial too (routes/qaRoutes.js qaAuthorization +
  // /api/qa/check-access), so this list cannot accidentally re-open it.
  // /scores is not in any of these lists: it is API-driven via
  // /api/scores/my-access. See claraf/src/constants/tlRoles.js.
  'kick-tl': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'tl', 'goals'],
  // 'vip-host-tl' (2026-09-03): a team leader on /tl and /goals exactly like
  // 'kick-tl' (no QA Manager), plus their own VIP Host Metrics page. Server
  // side: config/tlRoles.js TL_ROLES.
  'vip-host-tl': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'tl', 'goals', 'vip-host-metrics'],
  'audit': ['home', 'tools'],
  // 'ai-tester' (2026-09-19, Filip): was admin-equal on ONE tool — Ask Clara AI — while it
  // was in rollout. Since 2026-09-23 Clara AI, the ACP workbench and the Tool Builder are
  // open to every role, so the role grants nothing special any more; it stays valid for the
  // three logins that still carry it (rail = audit's shape) until they are moved back to 'user'.
  'ai-tester': ['home', 'tools'],
  // 'crm' (2026-09-22, Filip): the CRM team — everything a 'user' has, plus the Ladder Checker,
  // which is the only restricted tool that admits the role (toolsList.js RESTRICTED_TOOL_ROLES;
  // API twin routes/ladderRoutes.js LADDER_ROLES). Same rail as 'user' by design.
  'crm': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'goals'],
  // 'tl' on every QA role (2026-08-20): QA graders double as shift leaders
  // and write the Shift Leader Feedback on /tl → Reports. Like
  // kyc-admin / payments-admin above, reaching /tl grants nothing on its
  // own: without a TLTeamAssignment the dashboard is empty.
  'qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'qa-trainee': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'qa-admin': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'payments-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'payments-goals', 'tl'],
  // poker-qa = the Poker team's graders: payments-qa without Payments Goals.
  'poker-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'us-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'kyc-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'kyc-goals', 'goals', 'tl'],
  // kpp-qa is the kick-workspace twin of kyc-qa, minus kyc-goals (a KYC
  // surface) — its own grading lives on /qa-manager and /goals.
  // The four kick graders were added after the rule above and copied each
  // other's rail without 'tl' — given back 2026-10-03 (Filip: kick graders see
  // /tl → Presence too). The /tl API always admitted them (QA_ROLES).
  'kpp-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  // kick-mod-qa is the second kick-workspace grader role, same surfaces.
  'kick-mod-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  // kps-qa is the third kick-workspace grader role (Kick Partnership Serbia), same surfaces.
  'kps-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  // kick-qa is the plain Live Chat grader of the kick workspace (2026-09-10) — until now
  // those were role 'qa' moved into the workspace on the Access page.
  'kick-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  // host-qa / host-qa-admin are the hosts workspace's grader and QA admin (2026-09-10),
  // the same rail as 'qa' / 'qa-admin'.
  'host-qa': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'host-qa-admin': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'qa-manager', 'goals', 'tl'],
  'developer': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'developer-dashboard', 'monitoring', 'clara-ai-dashboard', 'qa-manager', 'kyc-goals', 'kyc-stats', 'payments-goals', 'crypto-goals', 'tl', 'goals', 'vip-host-metrics'],
  'admin': ['home', 'workspaces', 'tools', 'chat', 'knowledge-base', 'developer-dashboard', 'monitoring', 'clara-ai-dashboard', 'qa-manager', 'kyc-goals', 'kyc-stats', 'payments-goals', 'crypto-goals', 'tl', 'goals', 'vip-host-metrics', 'user-management'],
};

/**
 * Does this user see this page? ROLE is PRIMARY, pagePermissions is the
 * admin-set OVERRIDE (an explicit boolean wins in either direction). Admins
 * see everything; an unknown role falls back to the 'user' list.
 */
export function hasPagePermission(user, pageId) {
  const role = user?.role;
  const perms = user?.pagePermissions;

  // Simulations: hardcoded for now (config/simulationsAccess.js), no override either way — the API checks the same list
  if (pageId === 'simulations') return canUseSimulations(user);
  if (role === 'admin') return true;

  const rolePages = ROLE_ACCESS[role] || ROLE_ACCESS.user;
  const hasRoleAccess = rolePages.includes(pageId);

  if (perms && perms[pageId] && typeof perms[pageId].enabled === 'boolean') {
    return perms[pageId].enabled;
  }
  return hasRoleAccess;
}

/**
 * The user's effective set of rail pages, in NAV_ITEMS order. The three
 * API-driven ids take the answers the sidebar fetched; everything else goes
 * through hasPagePermission.
 */
export function visibleNavIds(user, { hasScoresAccess = false, hasEverythingAccess = false, hasAcpAccess = false } = {}) {
  return NAV_ITEMS.map((item) => item.id).filter((id) => {
    if (id === 'everything') return hasEverythingAccess;
    if (id === 'scores') return hasScoresAccess;
    if (id === 'acp') return hasAcpAccess;
    return hasPagePermission(user, id);
  });
}

/* ------------------------------ Tree ---------------------------------- */

/**
 * Turn a set of visible page ids into what the rail renders, top to bottom:
 *   { type: 'item',  id, iconName, label, path }
 *   { type: 'group', id, iconName, label, items: [item, …] }   (≥ 2 items)
 * See the grouping rule at the top of this file.
 */
export function buildNavTree(visibleIds) {
  const visible = new Set(visibleIds);
  const tree = [];

  for (const slot of NAV_ORDER) {
    if (slot.type === 'item') {
      if (visible.has(slot.id)) tree.push({ type: 'item', ...NAV_ITEM_BY_ID[slot.id] });
      continue;
    }
    const group = NAV_GROUP_BY_ID[slot.id];
    const items = group.items.filter((id) => visible.has(id)).map((id) => NAV_ITEM_BY_ID[id]);
    if (items.length === 0) continue;
    if (items.length === 1) {
      tree.push({ type: 'item', ...items[0] });
      continue;
    }
    tree.push({ type: 'group', id: group.id, iconName: group.iconName, label: group.label, items });
  }

  return tree;
}

/**
 * One line per rail entry, e.g. ["Home", "Metrics [Goals, KYC Goals]"]. The
 * per-role test asserts against this; it is also what you paste into a
 * message when someone asks "what does a payments-admin see".
 */
export function describeNavTree(tree) {
  return tree.map((entry) =>
    entry.type === 'group'
      ? `${entry.label} [${entry.items.map((item) => item.label).join(', ')}]`
      : entry.label
  );
}
